import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { EntradasService } from '../../../venta/services/entradas.service';
import { VentaService } from '../../../venta/services/venta.service';
import { StepperNavigationService } from '../../../venta/services/stepper-navigation.service';
import { AmigosThyssenService, ValidacionAmigoResponse } from '../../services/amigos-thyssen.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorToast } from 'src/app/core/toast/error.toast';
import { TranslateService } from 'src/app/shared/services/translate.service';

@Component({
  selector: 'amigos-thyssen-validacion',
  templateUrl: './validacion.component.html',
  styleUrls: ['./validacion.component.scss']
})
export class AmigosThyssenValidacionComponent implements OnInit, OnDestroy {
  form: FormGroup;
  buscando = false;
  errorMsg = '';
  validacion?: ValidacionAmigoResponse;
  categoriaSeleccionada?: any;
  subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private amigosSvc: AmigosThyssenService,
    private entradasService: EntradasService,
    private ventaService: VentaService,
    private router: Router,
    private stepperNavigationService: StepperNavigationService,
    private toast: ToastrService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      numeroAmigo: ['', [Validators.required]],
      dni: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Limpiar cualquier estado previo al inicializar
    this.limpiarValidacion();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  /**
   * Limpia completamente el estado de validación
   */
  limpiarValidacion(): void {
    this.validacion = undefined;
    this.form.reset();
    this.errorMsg = '';
    this.buscando = false;
    
    // Limpiar el amigo activo
    this.amigosSvc.clearAmigoActivo();
    this.ventaService.setClienteSeleccionadoValue('');
  }

  /**
   * Limpia solo el formulario manteniendo la validación
   */
  limpiarFormulario(): void {
    this.form.reset();
    this.errorMsg = '';
  }

  buscar(): void {
    if (this.form.invalid || this.buscando) return;
    
    // Cancelar cualquier suscripción anterior pendiente
    if (this.subs.length > 0) {
      this.subs.forEach(s => {
        if (!s.closed) {
          s.unsubscribe();
        }
      });
      this.subs = [];
    }
    
    // Limpiar la validación anterior antes de hacer la nueva búsqueda
    this.validacion = undefined;
    this.buscando = true;
    this.errorMsg = '';
    const numero = (this.form.value.numeroAmigo || '').toString().trim();
    const dni = (this.form.value.dni || '').toString().trim();
    
    const observable = this.amigosSvc.validarAmigo(numero, dni);
    
    const subscription = observable.subscribe({
        next: (res: ValidacionAmigoResponse) => {
          // Verificar si la validación fue exitosa
          if (!res || !res.datosResult) {
            // La validación falló, tratar como error
            this.validacion = undefined;
            this.errorMsg = 'DATOS_NO_VALIDOS';
            this.buscando = false;
            
            // Limpiar el amigo activo para ocultar la sección de entradas
            this.amigosSvc.clearAmigoActivo();
            
            // Limpiar el cliente seleccionado
            this.ventaService.setClienteSeleccionadoValue('');
            
            // Mostrar toast de error
            this.toast.error('', this.translate.instant('DATOS_NO_VALIDOS'), {
              toastComponent: ErrorToast,
              timeOut: 5000,
            });
            return;
          }

          // Configurar el cliente seleccionado para filtrar por cliente
          if (res.datosResult.iCodigoClienteVinculado) {
            // Configurar cliente con todos los campos necesarios para la inserción de venta
            this.ventaService.setClienteSeleccionadoValue({ 
              Codigo: res.datosResult.iCodigoClienteVinculado,
              // Campos necesarios para FormaPagoId, Descuento y GenerarFactura
              FP_Id: '1', // Forma de pago por defecto
              FP_Facturar: '0', // No generar factura por defecto
              ConDescuento: 'False', // Sin descuento por defecto
              Dto: '0' // Descuento 0 por defecto
            });
          }
          
          // Guardar namigo para adjuntarlo a las entradas que se añadan desde aquí
          const amigoAnterior = this.amigosSvc.getAmigoActivo();
          this.amigosSvc.setAmigoActivo({
            namigo: res.datosResult.namigo,
            iCodigoClienteVinculado: res.datosResult.iCodigoClienteVinculado
          });
          
          // NO cargar categorías aquí - el componente selector-mas-entradas las cargará
          // Esto evita llamadas duplicadas a GetKoreCategorias que pueden causar errores HTTP 0
          // cuando el inspector de red está abierto
          this.validacion = res;
          this.buscando = false;
          
          // Mostrar toast de éxito
          this.toast.success('', this.translate.instant('VALIDACION_EXITOSA'), {
            timeOut: 3000,
          });
        },
        error: (error) => {
          this.validacion = undefined;
          this.errorMsg = 'DATOS_NO_VALIDOS';
          this.buscando = false;
          
          // Limpiar el amigo activo para ocultar la sección de entradas
          this.amigosSvc.clearAmigoActivo();
          
          // Limpiar el cliente seleccionado
          this.ventaService.setClienteSeleccionadoValue('');
          
          // Mostrar toast de error usando el servicio
          this.toast.error('', this.translate.instant('DATOS_NO_VALIDOS'), {
            toastComponent: ErrorToast,
            timeOut: 5000,
          });
        }
      });
  }

  /**
   * Añade una entrada al carrito con el namigo del amigo thyssen
   * @param entrada - La entrada o tarifa a añadir
   */
  anadirEntrada(entrada: any): void {
    const amigo = this.amigosSvc.getAmigoActivo();
    if (!amigo) {
      // Mostrar toast de error
      this.toast.error('', this.translate.instant('NO_AMIGO_ACTIVO'), {
        toastComponent: ErrorToast,
        timeOut: 5000,
      });
      return;
    }

    // Crear la entrada con el namigo en DNIAbonadosExt
    const entradaConNamigo = {
      ...entrada,
      DNIAbonadosExt: amigo.namigo,
      // Asegurar que se identifique como entrada de amigos thyssen
      esAmigoThyssen: true,
      amigoThyssen: amigo
    };

    // Añadir la entrada al carrito usando el servicio de venta
    this.ventaService.sendEntrada(entradaConNamigo);
    
    // Mostrar confirmación visual (opcional)
    this.mostrarConfirmacionEntrada(entrada);
  }

  /**
   * Muestra confirmación visual de que se añadió la entrada
   */
  private mostrarConfirmacionEntrada(entrada: any): void {
    // Mostrar toast de confirmación
    this.toast.success('', this.translate.instant('ENTRADA_ANADIDA_CARRITO'), {
      timeOut: 2000,
    });
  }

  /**
   * Verifica si hay stock disponible para una entrada/tarifa
   */
  hayStockDisponible(entrada: any): boolean {
    // Implementar lógica de verificación de stock si es necesario
    // Por defecto, asumimos que hay stock disponible
    return true;
  }

  /**
   * Obtiene el número de entradas en el carrito para este amigo
   */
  getNumeroEntradasEnCarrito(): number {
    const carrito = this.ventaService.carritoValue;
    const amigo = this.amigosSvc.getAmigoActivo();
    
    if (!carrito || !carrito.entradas || !amigo) return 0;
    
    return carrito.entradas.filter(entrada => 
      entrada.entrada.DNIAbonadosExt === amigo.namigo
    ).length;
  }

  irASiguiente(): void {
    // Continuar al siguiente paso usando el servicio de navegación
    this.stepperNavigationService.continuarVenta.next(true);
    this.stepperNavigationService.next();
  }

  seleccionarCategoria(cat: any): void {
    this.categoriaSeleccionada = cat;
  }

  /**
   * Método para limpiar y volver al estado inicial
   * Se puede llamar desde el componente padre o desde navegación
   */
  resetearComponente(): void {
    this.limpiarValidacion();
  }
}
